import React from "react";
import { Banner, Button, Dialog, FormField, Select } from "@/ds";
import { cockpit, type RequestDetail, type WorkflowSwitchPreview, type WorkflowSwitchCommand } from "@/api";
import { useT } from "../../i18n";
import { sameWorkflowProfile } from "../../lib/workflow-profile";

export function WorkflowSwitch({ data, reload, disabled }: { data: RequestDetail; reload: () => Promise<unknown>; disabled: boolean }) {
  const t = useT();
  const [open, setOpen] = React.useState(false);
  const [preview, setPreview] = React.useState<WorkflowSwitchPreview | null>(null);
  const [engine, setEngine] = React.useState("deterministic");
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const command = React.useRef<WorkflowSwitchCommand | null>(null);
  const load = async () => {
    setOpen(true); setBusy(true); setError(null); setPreview(null); command.current = null;
    try { const p = await cockpit.previewWorkflowSwitch(data.request.id); setPreview(p); setEngine(p.current_pin.engine === "single_agent" ? "deterministic" : "single_agent"); }
    catch { setError(t("workflow.loadFailed")); } finally { setBusy(false); }
  };
  const target = preview?.options.find(o => o.engine === engine);
  const change = async () => {
    if (!preview || !target?.profile || !target.available) return;
    setBusy(true); setError(null);
    command.current ??= { engine: target.engine, profile_hash: target.profile.profile_hash,
      expected_revision: preview.expected_revision, expected_engine_epoch: preview.current_pin.engine_epoch, idempotency_key: crypto.randomUUID() };
    try { await cockpit.switchWorkflow(data.request.id, command.current); await reload(); setOpen(false); }
    catch (e) { const code = (e as { data?: { code?: string } }).data?.code; setError(t(`workflow.error.${code}`, undefined, t("workflow.conflict"))); }
    finally { setBusy(false); }
  };
  return <>
    <Button size="sm" variant="secondary" disabled={disabled} onClick={() => void load()}>{t("workflow.switch")}</Button>
    <Dialog closeButton={false} open={open} onClose={() => { if (!busy) setOpen(false); }} title={t("workflow.switch")} footer={<>
      <Button variant="ghost" disabled={busy} onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
      <Button variant="primary" disabled={busy || !preview?.eligible || !target?.available || !target.profile || sameWorkflowProfile(target.profile, preview.current_pin)} onClick={() => void change()}>{t("workflow.switchConfirm")}</Button>
    </>}>
      <div className="stack-sm" aria-busy={busy}>
        {error && <Banner tone="danger" title={t("common.error")}>{error}</Banner>}
        {!preview && !error && <p>{t("common.loading")}</p>}
        {preview && <>
          <p>{t("workflow.mode")}: {t(`workflow.${preview.current_pin.engine}`)} · {preview.current_pin.profile_id} {preview.current_pin.profile_version}</p>
          <FormField label={t("workflow.target")}><Select value={engine} disabled={busy} onChange={e => { setEngine(e.target.value); command.current = null; }}
            options={preview.options.map(o => ({ value: o.engine, label: t(`workflow.${o.engine}`) }))} /></FormField>
          <p>{target?.profile?.profile_id} {target?.profile?.profile_version}</p>
          {!target?.available && <p>{t(`workflow.error.${target?.reason}`, undefined, t("workflow.notReady"))}</p>}
          {!preview.eligible && <p>{t("workflow.error.request_read_only")}</p>}
          <p>{t("workflow.switchConsequences")}</p>
          {engine === "single_agent" && <p>{t("workflow.paidChanges")}</p>}
          {engine === "deterministic" && preview.preserved_non_authoritative_fields.length > 0 && <p>{t("workflow.compatibility")}: {preview.preserved_non_authoritative_fields.map(f => t(`field.${f}`, undefined, f)).join(", ")}</p>}
          {preview.outstanding_reservations.length > 0 && <p>{t("workflow.reservesRetained", { n: preview.outstanding_reservations.length })}</p>}
        </>}
      </div>
    </Dialog>
  </>;
}
